import {
  BanknoteIcon,
  BriefcaseIcon,
  BuildingIcon,
  Building2Icon,
  ClipboardCheckIcon,
  CoinsIcon,
  CompassIcon,
  FactoryIcon,
  FileSearchIcon,
  GlobeIcon,
  HandshakeIcon,
  HardHatIcon,
  HeartPulseIcon,
  HotelIcon,
  LandmarkIcon,
  LeafIcon,
  LineChartIcon,
  LockIcon,
  MessageSquareIcon,
  PlugZapIcon,
  ScaleIcon,
  ShipIcon,
  ShoppingBagIcon,
  SignatureIcon,
  SparklesIcon,
  TimerIcon,
  TrendingUpIcon,
  TruckIcon,
  UserCheckIcon,
  UsersIcon,
  WalletIcon,
  CpuIcon,
} from 'lucide-react'
import type { Lang } from '../i18n/LanguageContext'

export type IconType = typeof GlobeIcon

type L<T> = Record<Lang, T>

export const DISCLAIMER: L<string> = {
  es: 'Actuamos únicamente como compañía independiente de intermediación financiera y captación de capital. No prestamos servicios bancarios ni concedemos fondos directamente salvo que se indique expresamente. Todas las oportunidades de financiación e inversión están sujetas a revisión independiente, aprobación y a los términos de las entidades financieras, prestamistas e inversores participantes.',
  en: 'We act solely as an independent financial introduction and capital sourcing company. We do not provide banking services or lend funds directly unless expressly stated. All financing and investment opportunities are subject to independent review, approval, and the terms of participating financial institutions, lenders, and investors.',
}

type TextItem = { title: string; description: string }

export const HOME_SERVICES: { Icon: IconType; copy: L<TextItem> }[] = [
  {
    Icon: BanknoteIcon,
    copy: {
      es: {
        title: 'Intermediación de Préstamos',
        description:
          'Presentaciones estructuradas a bancos, fondos de crédito y prestamistas especializados según su sector, jurisdicción y garantías.',
      },
      en: {
        title: 'Loan Introductions',
        description:
          'Structured introductions to banks, credit funds and specialist lenders aligned to your sector, jurisdiction and security profile.',
      },
    },
  },
  {
    Icon: TrendingUpIcon,
    copy: {
      es: {
        title: 'Intermediación de Inversión',
        description:
          'Acceso a family offices, fondos institucionales, inversores privados y socios estratégicos que están invirtiendo activamente.',
      },
      en: {
        title: 'Investment Introductions',
        description:
          'Access to family offices, institutional funds, private investors and strategic partners actively deploying capital.',
      },
    },
  },
  {
    Icon: CoinsIcon,
    copy: {
      es: {
        title: 'Captación de Capital',
        description:
          'Apoyo en el posicionamiento de rondas de deuda, capital o híbridas para presentar su propuesta al capital adecuado en el momento adecuado.',
      },
      en: {
        title: 'Capital Raising',
        description:
          'Support in positioning debt, equity and hybrid raises so your proposition is presented to the right capital at the right time.',
      },
    },
  },
  {
    Icon: CompassIcon,
    copy: {
      es: {
        title: 'Asesoramiento Empresarial',
        description:
          'Orientación independiente sobre estrategia, estructura y preparación para inversores antes de que su expediente llegue al mercado.',
      },
      en: {
        title: 'Business Advisory',
        description:
          'Independent guidance on funding strategy, structure and investment readiness before your file reaches the market.',
      },
    },
  },
  {
    Icon: Building2Icon,
    copy: {
      es: {
        title: 'Financiación de Proyectos',
        description:
          'Presentaciones para proyectos de infraestructuras, energía y promoción que requieren soluciones estructuradas a largo plazo.',
      },
      en: {
        title: 'Project Finance',
        description:
          'Introductions for infrastructure, energy and development projects requiring long-term, structured funding solutions.',
      },
    },
  },
  {
    Icon: ShipIcon,
    copy: {
      es: {
        title: 'Financiación de Comercio',
        description:
          'Créditos documentarios, avales, descuento de facturas y líneas de cadena de suministro con financiadores internacionales.',
      },
      en: {
        title: 'Trade Finance',
        description:
          'Letters of credit, guarantees, receivables and supply chain facilities via internationally active trade financiers.',
      },
    },
  },
  {
    Icon: BriefcaseIcon,
    copy: {
      es: {
        title: 'Private Equity',
        description:
          'Conexiones con fondos de crecimiento, adquisición y situaciones especiales que buscan oportunidades cualificadas en su mercado.',
      },
      en: {
        title: 'Private Equity',
        description:
          'Connections to growth, buyout and special situations funds seeking qualified opportunities in your market.',
      },
    },
  },
  {
    Icon: WalletIcon,
    copy: {
      es: {
        title: 'Circulante',
        description:
          'Líneas revolving, factoring y liquidez a corto plazo para sostener la operativa diaria y el crecimiento.',
      },
      en: {
        title: 'Working Capital',
        description:
          'Revolving facilities, invoice finance and short-term liquidity lines to support day-to-day operations and growth.',
      },
    },
  },
]

export const INDUSTRIES: { Icon: IconType; name: L<string> }[] = [
  { Icon: HardHatIcon, name: { es: 'Construcción', en: 'Construction' } },
  { Icon: BuildingIcon, name: { es: 'Inmobiliario', en: 'Real Estate' } },
  { Icon: HeartPulseIcon, name: { es: 'Salud', en: 'Healthcare' } },
  { Icon: CpuIcon, name: { es: 'Tecnología', en: 'Technology' } },
  { Icon: FactoryIcon, name: { es: 'Industria', en: 'Manufacturing' } },
  { Icon: LeafIcon, name: { es: 'Agricultura', en: 'Agriculture' } },
  { Icon: ShoppingBagIcon, name: { es: 'Retail', en: 'Retail' } },
  { Icon: HotelIcon, name: { es: 'Hostelería y Turismo', en: 'Hospitality' } },
  { Icon: PlugZapIcon, name: { es: 'Energía', en: 'Energy' } },
  { Icon: Building2Icon, name: { es: 'Infraestructuras', en: 'Infrastructure' } },
  { Icon: TruckIcon, name: { es: 'Logística', en: 'Logistics' } },
  { Icon: LandmarkIcon, name: { es: 'Contratistas Públicos', en: 'Government Contractors' } },
]

export const WHY_US: { Icon: IconType; copy: L<TextItem> }[] = [
  {
    Icon: UsersIcon,
    copy: {
      es: {
        title: 'Equipo Profesional',
        description:
          'Antiguos banqueros, analistas de riesgos y asesores de finanzas corporativas que entienden cómo evalúan el riesgo los prestamistas e inversores.',
      },
      en: {
        title: 'Professional Team',
        description:
          'Former bankers, credit analysts and corporate finance advisers who understand how lenders and investors assess risk.',
      },
    },
  },
  {
    Icon: GlobeIcon,
    copy: {
      es: {
        title: 'Red Global',
        description:
          'Más de 75 socios financieros verificados entre bancos comerciales, fondos de deuda privada, family offices e inversores institucionales.',
      },
      en: {
        title: 'Global Network',
        description:
          '75+ vetted funding partners spanning commercial banks, private credit funds, family offices and institutional investors.',
      },
    },
  },
  {
    Icon: LockIcon,
    copy: {
      es: {
        title: 'Proceso Confidencial',
        description:
          'Su información se gestiona con estricta confidencialidad y solo se comparte con su autorización por escrito.',
      },
      en: {
        title: 'Confidential Process',
        description:
          'Your information is handled under strict confidentiality and released only with your written authorisation.',
      },
    },
  },
  {
    Icon: TimerIcon,
    copy: {
      es: {
        title: 'Presentaciones Rápidas',
        description:
          'Las consultas completas se revisan normalmente en 48 horas y se emparejan con socios relevantes poco después.',
      },
      en: {
        title: 'Fast Introductions',
        description:
          'Complete enquiries are typically reviewed within 48 hours and matched to relevant partners shortly thereafter.',
      },
    },
  },
  {
    Icon: CompassIcon,
    copy: {
      es: {
        title: 'Alcance Internacional',
        description:
          'Experiencia transfronteriza en más de 40 países, incluidas estructuras multidivisa y multijurisdicción.',
      },
      en: {
        title: 'International Reach',
        description:
          'Cross-border experience across 40+ countries, including multi-currency and multi-jurisdiction structures.',
      },
    },
  },
  {
    Icon: MessageSquareIcon,
    copy: {
      es: {
        title: 'Comunicación Transparente',
        description:
          'Alcance claro, honorarios claros y una valoración honesta, incluso cuando creemos que una propuesta no es financiable.',
      },
      en: {
        title: 'Transparent Communication',
        description:
          'Clear scope, clear fees and honest feedback — including when we believe a proposal is not yet fundable.',
      },
    },
  },
]

export const SIMPLE_PROCESS: L<string>[] = [
  { es: 'Envío de la consulta', en: 'Submit enquiry' },
  { es: 'Revisión inicial', en: 'Initial review' },
  { es: 'Documentación', en: 'Documentation' },
  { es: 'Emparejamiento con socios', en: 'Funding partner matching' },
  { es: 'Due diligence', en: 'Due diligence' },
  { es: 'Negociación', en: 'Negotiation' },
  { es: 'Cierre de la financiación', en: 'Funding completion' },
]

export const STATS: { value: string; label: L<string> }[] = [
  { value: '75+', label: { es: 'Socios Financieros', en: 'Funding Partners' } },
  { value: '40+', label: { es: 'Países', en: 'Countries' } },
  { value: '2.000+', label: { es: 'Consultas de Financiación', en: 'Funding Enquiries' } },
  { value: '15+', label: { es: 'Años de Experiencia Combinada', en: 'Years Combined Experience' } },
]

export const TESTIMONIALS: {
  name: string
  copy: L<{ quote: string; role: string; location: string }>
}[] = [
  {
    name: 'A. Herrero',
    copy: {
      es: {
        quote:
          'Entendieron nuestra cartera de promociones de inmediato y nos presentaron a dos financiadores de deuda senior en quince días. El proceso fue discreto, estructurado y verdaderamente profesional.',
        role: 'Director General, Grupo de Promoción Inmobiliaria',
        location: 'España',
      },
      en: {
        quote:
          'Their team understood our development pipeline immediately and introduced us to two senior debt providers within a fortnight. The process was discreet, structured and genuinely professional.',
        role: 'Managing Director, Property Development Group',
        location: 'Spain',
      },
    },
  },
  {
    name: 'M. Haddad',
    copy: {
      es: {
        quote:
          'Durante un año habíamos estado hablando con el tipo de inversor equivocado. Solo el consejo de posicionamiento cambió la conversación, y las presentaciones posteriores culminaron en una ronda de capital de crecimiento.',
        role: 'Fundador y CEO, Tecnología Sanitaria',
        location: 'Emiratos Árabes Unidos',
      },
      en: {
        quote:
          'We had been approaching the wrong type of investor for a year. The positioning advice alone changed the conversation, and the introductions that followed led to a completed growth equity round.',
        role: 'Founder & CEO, Healthcare Technology',
        location: 'United Arab Emirates',
      },
    },
  },
  {
    name: 'L. Okonkwo',
    copy: {
      es: {
        quote:
          'Las líneas de comercio transfronterizo rara vez son sencillas. Contar con un intermediario capaz de hablar el idioma de nuestro consejo y del comité de riesgos marcó la diferencia.',
        role: 'Director Financiero de Grupo, Exportadora Agrícola',
        location: 'Nigeria',
      },
      en: {
        quote:
          'Cross-border trade facilities are rarely straightforward. Having an intermediary who could speak the language of both our board and the credit committee made the difference.',
        role: 'Group CFO, Agricultural Exporter',
        location: 'Nigeria',
      },
    },
  },
]

export const PROCESS_TIMELINE: {
  Icon: IconType
  duration: L<string>
  copy: L<TextItem>
}[] = [
  {
    Icon: MessageSquareIcon,
    duration: { es: 'Día 1–2', en: 'Day 1–2' },
    copy: {
      es: {
        title: 'Consulta Inicial',
        description:
          'Una conversación confidencial sobre sus objetivos, el importe buscado, el destino de los fondos, los plazos y la jurisdicción. En esta fase confirmamos si podemos ayudarle.',
      },
      en: {
        title: 'Initial Consultation',
        description:
          'A confidential discussion covering your objectives, the amount sought, intended use of funds, timing and jurisdiction. We confirm at this stage whether we are able to assist.',
      },
    },
  },
  {
    Icon: FileSearchIcon,
    duration: { es: 'Día 2–5', en: 'Day 2–5' },
    copy: {
      es: {
        title: 'Evaluación de la Financiación',
        description:
          'Analizamos la viabilidad frente al apetito actual de prestamistas e inversores, identificamos estructuras probables y señalamos las carencias que debilitarían la propuesta.',
      },
      en: {
        title: 'Funding Assessment',
        description:
          'We assess viability against current lender and investor appetite, identify likely structures and highlight any gaps that would weaken the proposal.',
      },
    },
  },
  {
    Icon: ClipboardCheckIcon,
    duration: { es: 'Semana 1–2', en: 'Week 1–2' },
    copy: {
      es: {
        title: 'Revisión Documental',
        description:
          'Revisamos cuentas, plan de negocio, proyecciones, garantías y documentación societaria y los organizamos en un expediente claro y creíble.',
      },
      en: {
        title: 'Document Review',
        description:
          'Financials, business plan, projections, security schedule and corporate documents are reviewed and packaged into a clear, credible funding file.',
      },
    },
  },
  {
    Icon: ScaleIcon,
    duration: { es: 'Semana 2–3', en: 'Week 2–3' },
    copy: {
      es: {
        title: 'Emparejamiento',
        description:
          'Cruzamos su necesidad con nuestra red de bancos, fondos de crédito, inversores privados y socios institucionales para seleccionar el mejor encaje.',
      },
      en: {
        title: 'Funding Match',
        description:
          'Your requirement is mapped against our network of banks, credit funds, private investors and institutional partners to shortlist the most relevant fit.',
      },
    },
  },
  {
    Icon: HandshakeIcon,
    duration: { es: 'Semana 3–4', en: 'Week 3–4' },
    copy: {
      es: {
        title: 'Presentación al Financiador',
        description:
          'Con su autorización por escrito, realizamos las presentaciones formales y acompañamos el diálogo inicial con cada socio financiero.',
      },
      en: {
        title: 'Lender Introduction',
        description:
          'With your written authorisation, we make formal introductions and support the initial dialogue between you and each funding partner.',
      },
    },
  },
  {
    Icon: LineChartIcon,
    duration: { es: 'Semana 4–8', en: 'Week 4–8' },
    copy: {
      es: {
        title: 'Negociación',
        description:
          'Permanecemos disponibles durante las discusiones del term sheet, la due diligence y las aclaraciones, coordinando a todas las partes.',
      },
      en: {
        title: 'Negotiation',
        description:
          'We remain available throughout term sheet discussions, due diligence and clarification requests, coordinating between all parties.',
      },
    },
  },
  {
    Icon: SignatureIcon,
    duration: { es: 'Semana 8+', en: 'Week 8+' },
    copy: {
      es: {
        title: 'Cierre',
        description:
          'La aprobación final, la documentación legal y la disposición de fondos se formalizan directamente entre usted y el socio financiero, con nuestro apoyo continuado.',
      },
      en: {
        title: 'Completion',
        description:
          'Final approval, legal documentation and drawdown are completed directly between you and the funding partner, with our continued support.',
      },
    },
  },
]

export const VALUES: { Icon: IconType; copy: L<TextItem> }[] = [
  {
    Icon: ScaleIcon,
    copy: {
      es: {
        title: 'Integridad Ante Todo',
        description:
          'Rechazamos los mandatos que no podemos atender con credibilidad. Una valoración honesta protege su tiempo, su reputación y la nuestra.',
      },
      en: {
        title: 'Integrity First',
        description:
          'We decline mandates we cannot serve credibly. Honest assessment protects your time, your reputation and ours.',
      },
    },
  },
  {
    Icon: LockIcon,
    copy: {
      es: {
        title: 'Confidencialidad Absoluta',
        description:
          'La información comercial sensible se compartimenta y solo se comparte con su consentimiento expreso por escrito.',
      },
      en: {
        title: 'Absolute Confidentiality',
        description:
          'Sensitive commercial information is compartmentalised and shared only with your explicit written consent.',
      },
    },
  },
  {
    Icon: UserCheckIcon,
    copy: {
      es: {
        title: 'Alineación con el Cliente',
        description:
          'Tenemos éxito cuando el capital adecuado encuentra la oportunidad adecuada, nunca empujando una estructura inapropiada.',
      },
      en: {
        title: 'Client Alignment',
        description:
          'We succeed when the right capital meets the right opportunity — never by pushing an unsuitable structure.',
      },
    },
  },
  {
    Icon: GlobeIcon,
    copy: {
      es: {
        title: 'Perspectiva Global',
        description:
          'El capital es internacional. Pensamos en jurisdicciones, divisas y estructuras transfronterizas de forma natural.',
      },
      en: {
        title: 'Global Perspective',
        description:
          'Capital is international. We think in jurisdictions, currencies and cross-border structures as a matter of course.',
      },
    },
  },
  {
    Icon: SparklesIcon,
    copy: {
      es: {
        title: 'Precisión y Oficio',
        description:
          'Los expedientes bien preparados obtienen mejores condiciones. Mantenemos nuestros estándares al nivel institucional.',
      },
      en: {
        title: 'Precision & Craft',
        description:
          'Well-prepared files receive better terms. We hold our documentation standards to institutional expectations.',
      },
    },
  },
  {
    Icon: HandshakeIcon,
    copy: {
      es: {
        title: 'Relaciones Duraderas',
        description:
          'La mayor parte de nuestro trabajo proviene de clientes recurrentes y de recomendaciones de socios construidas durante más de una década.',
      },
      en: {
        title: 'Enduring Relationships',
        description:
          'Most of our work comes from repeat clients and partner referrals built over more than a decade.',
      },
    },
  },
]

export type ServiceGroup = {
  Icon: IconType
  copy: L<{
    title: string
    intro: string
    items: { name: string; description: string }[]
  }>
}

export const SERVICE_GROUPS: ServiceGroup[] = [
  {
    Icon: BanknoteIcon,
    copy: {
      es: {
        title: 'Intermediación de Préstamos',
        intro:
          'Presentaciones de deuda a bancos comerciales, fondos de crédito privado, financiadores de activos y prestamistas especializados de nuestra red internacional.',
        items: [
          { name: 'Préstamos Empresariales', description: 'Financiación a plazo para crecimiento, reestructuración y expansión.' },
          { name: 'Circulante', description: 'Líneas revolving y liquidez para la operativa diaria.' },
          { name: 'Financiación de Comercio', description: 'Créditos documentarios, avales y cadena de suministro.' },
          { name: 'Financiación de Activos', description: 'Financiación garantizada con maquinaria, vehículos y equipos.' },
          { name: 'Financiación de Proyectos', description: 'Estructuras a largo plazo para grandes inversiones.' },
          { name: 'Financiación Comercial', description: 'Operaciones con garantía inmobiliaria o de créditos.' },
          { name: 'Financiación de Construcción', description: 'Financiación de promoción con disposiciones por hitos.' },
          { name: 'Importación y Exportación', description: 'Líneas transfronterizas para el comercio internacional.' },
          { name: 'Financiación de Equipos', description: 'Leasing y renting para bienes de equipo.' },
          { name: 'Préstamos Corporativos', description: 'Operaciones bilaterales y sindicadas de mayor tamaño.' },
        ],
      },
      en: {
        title: 'Loan Introduction',
        intro:
          'Debt introductions to commercial banks, private credit funds, asset-based lenders and specialist financiers across our international network.',
        items: [
          { name: 'Business Loans', description: 'Term facilities for growth, restructuring and expansion.' },
          { name: 'Working Capital', description: 'Revolving lines and liquidity support for daily operations.' },
          { name: 'Trade Finance', description: 'Letters of credit, guarantees and supply chain facilities.' },
          { name: 'Asset Finance', description: 'Funding secured against plant, vehicles and equipment.' },
          { name: 'Project Finance', description: 'Long-term structured funding for large capital projects.' },
          { name: 'Commercial Finance', description: 'Facilities secured on commercial property and receivables.' },
          { name: 'Construction Finance', description: 'Development and build-out funding released in stages.' },
          { name: 'Import & Export Finance', description: 'Cross-border facilities supporting international trade.' },
          { name: 'Equipment Finance', description: 'Lease and hire purchase structures for capital equipment.' },
          { name: 'Corporate Lending', description: 'Larger syndicated and bilateral corporate facilities.' },
        ],
      },
    },
  },
  {
    Icon: TrendingUpIcon,
    copy: {
      es: {
        title: 'Intermediación de Inversión',
        intro:
          'Presentaciones de capital y socios estratégicos a inversores que buscan activamente oportunidades cualificadas en su sector y región.',
        items: [
          { name: 'Private Equity', description: 'Fondos de crecimiento, adquisición y situaciones especiales.' },
          { name: 'Capital Riesgo', description: 'Inversores de tecnología e innovación en fases temprana y de crecimiento.' },
          { name: 'Business Angels', description: 'Inversores privados con experiencia que respaldan proyectos escalables.' },
          { name: 'Family Offices', description: 'Capital privado con horizonte largo y participación directa.' },
          { name: 'Inversores Institucionales', description: 'Fondos, aseguradoras y capital de pensiones a gran escala.' },
          { name: 'Joint Ventures', description: 'Socios para estructuras de proyecto con riesgo compartido.' },
          { name: 'Captación de Capital', description: 'Posicionamiento de rondas de deuda, capital o híbridas.' },
          { name: 'Inversión Estratégica', description: 'Socios industriales que aportan más que capital.' },
          { name: 'Financiación de Adquisiciones', description: 'Estructuras de capital para M&A y MBO.' },
        ],
      },
      en: {
        title: 'Investment Introduction',
        intro:
          'Equity and strategic capital introductions to investors actively seeking qualified opportunities in your sector and region.',
        items: [
          { name: 'Private Equity', description: 'Growth, buyout and special situations funds.' },
          { name: 'Venture Capital', description: 'Early and growth-stage technology and innovation investors.' },
          { name: 'Angel Investors', description: 'Experienced private individuals backing scalable ventures.' },
          { name: 'Family Offices', description: 'Long-horizon private capital seeking direct participation.' },
          { name: 'Institutional Investors', description: 'Funds, insurers and pension capital at scale.' },
          { name: 'Joint Ventures', description: 'Partner introductions for shared-risk project structures.' },
          { name: 'Capital Raising', description: 'Debt, equity and hybrid raise positioning and outreach.' },
          { name: 'Strategic Investment', description: 'Corporate and industry partners bringing more than capital.' },
          { name: 'Business Acquisition Funding', description: 'Capital structures supporting M&A and MBOs.' },
        ],
      },
    },
  },
  {
    Icon: CompassIcon,
    copy: {
      es: {
        title: 'Asesoramiento',
        intro:
          'Preparación y estrategia independientes que mejoran de forma sustancial cómo reciben su propuesta los prestamistas e inversores.',
        items: [
          { name: 'Estrategia de Financiación', description: 'El instrumento, la secuencia y el momento adecuados.' },
          { name: 'Consultoría de Negocio', description: 'Revisión comercial del modelo, márgenes y escalabilidad.' },
          { name: 'Preparación para Inversores', description: 'Gobernanza, reporting y preparación del data room.' },
          { name: 'Estructuración del Expediente', description: 'Dosieres listos para comité de riesgos.' },
          { name: 'Revisión del Plan de Negocio', description: 'Contraste de hipótesis, previsiones y relato.' },
          { name: 'Asesoramiento Transfronterizo', description: 'Jurisdicción, divisa y consideraciones de estructura.' },
        ],
      },
      en: {
        title: 'Advisory',
        intro:
          'Independent preparation and strategy work that materially improves how lenders and investors receive your proposition.',
        items: [
          { name: 'Funding Strategy', description: 'The right instrument, sequence and timing for your raise.' },
          { name: 'Business Consulting', description: 'Commercial review of model, margins and scalability.' },
          { name: 'Investment Readiness', description: 'Governance, reporting and data room preparation.' },
          { name: 'Loan Packaging', description: 'Credit-committee-ready files and structured presentation.' },
          { name: 'Business Plan Review', description: 'Stress-testing assumptions, forecasts and narrative.' },
          { name: 'Cross-border Advisory', description: 'Jurisdiction, currency and structuring considerations.' },
        ],
      },
    },
  },
]

export const FAQS: L<{ question: string; answer: string }>[] = [
  {
    es: {
      question: '¿Son ustedes prestamistas?',
      answer:
        'No. Somos una compañía independiente de intermediación de préstamos e inversión. No prestamos fondos propios y no somos un banco. Nuestra función es evaluar su necesidad y presentarle a prestamistas, bancos, fondos e inversores apropiados de nuestra red.',
    },
    en: {
      question: 'Are you a lender?',
      answer:
        'No. We are an independent loan and investment introduction company. We do not lend our own funds and we are not a bank. Our role is to assess your requirement and introduce you to appropriate lenders, banks, funds and investors within our network.',
    },
  },
  {
    es: {
      question: '¿Garantizan la financiación?',
      answer:
        'No. Ningún intermediario serio puede garantizar financiación. Todas las oportunidades están sujetas a revisión independiente, aprobación de riesgos y a los términos de las entidades, prestamistas e inversores participantes. Lo que sí garantizamos es una evaluación honesta y, cuando es viable, presentaciones relevantes.',
    },
    en: {
      question: 'Do you guarantee funding?',
      answer:
        'No. No credible intermediary can guarantee funding. All financing and investment opportunities are subject to independent review, credit approval and the terms of participating institutions, lenders and investors. What we do commit to is an honest assessment and, where viable, relevant introductions.',
    },
  },
  {
    es: {
      question: '¿Quién puede solicitarlo?',
      answer:
        'Empresas, emprendedores, promotores, patrocinadores de proyectos e inversores con una necesidad de financiación claramente definida. Trabajamos habitualmente con negocios consolidados y promotores solventes que pueden aportar documentación de soporte.',
    },
    en: {
      question: 'Who can apply?',
      answer:
        'Companies, entrepreneurs, developers, project sponsors and investors with a clearly defined funding requirement. We typically work with established businesses and credible project sponsors who can provide supporting documentation.',
    },
  },
  {
    es: {
      question: '¿A qué países prestan servicio?',
      answer:
        'Nuestra red de socios abarca más de 40 países en Europa, Oriente Medio, África, Asia, Norteamérica y Latinoamérica, con España como mercado principal. La disponibilidad varía según jurisdicción, sector y consideraciones de sanciones, algo que confirmamos en la revisión inicial.',
    },
    en: {
      question: 'Which countries do you serve?',
      answer:
        'Our partner network spans more than 40 countries across Europe, the Middle East, Africa, Asia, North America and Latin America, with Spain as our home market. Availability varies by jurisdiction, sector and sanctions considerations, which we confirm at the initial review stage.',
    },
  },
  {
    es: {
      question: '¿Cuánto tarda el proceso?',
      answer:
        'La revisión inicial suele tardar 48 horas. El emparejamiento y las presentaciones se producen normalmente entre dos y cuatro semanas desde la recepción de la documentación completa. Los plazos de cierre los determina el socio financiero y la complejidad de la operación, habitualmente entre seis semanas y varios meses.',
    },
    en: {
      question: 'How long does the process take?',
      answer:
        'Initial review typically takes 48 hours. Matching and introductions usually occur within two to four weeks of receiving complete documentation. Completion timelines are determined by the funding partner and the complexity of the transaction, and commonly range from six weeks to several months.',
    },
  },
  {
    es: {
      question: '¿Qué documentación se requiere?',
      answer:
        'Normalmente documentación societaria, dos o tres años de cuentas anuales o estados de gestión, plan de negocio o resumen del proyecto, proyecciones financieras, detalle del importe y destino de los fondos y de las garantías disponibles.',
    },
    en: {
      question: 'What documentation is required?',
      answer:
        'Usually company registration documents, two to three years of financial statements or management accounts, a business plan or project summary, financial projections, details of the amount and purpose of funding, and details of any available security or collateral.',
    },
  },
  {
    es: {
      question: '¿Sus servicios son confidenciales?',
      answer:
        'Sí. Todas las consultas se tratan con absoluta confidencialidad. La información se comparte con socios financieros únicamente tras su autorización por escrito y firmamos acuerdos de confidencialidad si así lo solicita.',
    },
    en: {
      question: 'Are your services confidential?',
      answer:
        'Yes. All enquiries are treated as strictly confidential. Information is shared with funding partners only after you provide written authorisation, and we are willing to sign a non-disclosure agreement on request.',
    },
  },
  {
    es: {
      question: '¿Trabajan con startups?',
      answer:
        'De forma selectiva. Los negocios sin ingresos suelen encajar mejor con capital, capital riesgo o business angels que con deuda bancaria. Le diremos con franqueza si su fase todavía no se ajusta al apetito actual de los inversores.',
    },
    en: {
      question: 'Do you work with startups?',
      answer:
        'Selectively. Pre-revenue businesses are generally better suited to equity, venture capital or angel introductions rather than bank debt. We will tell you candidly if your stage does not yet fit current investor appetite.',
    },
  },
  {
    es: {
      question: '¿Cómo se cobran los honorarios?',
      answer:
        'Los honorarios se acuerdan por escrito antes de iniciar cualquier trabajo. Según el alcance, pueden incluir una cuota de preparación o asesoramiento y un honorario de éxito pagadero al cierre. Nunca practicamos deducciones no explicadas sobre los importes financiados.',
    },
    en: {
      question: 'How are fees charged?',
      answer:
        'Fees are agreed in writing before any work begins. Depending on scope, this may include a preparation or advisory fee and a success-based introduction fee payable on completion. We never take unexplained deductions from funded amounts.',
    },
  },
  {
    es: {
      question: '¿Cuál es el importe mínimo que consideran?',
      answer:
        'Nos centramos habitualmente en necesidades desde 500.000 € en adelante, sin límite máximo formal. Podemos considerar importes menores cuando la estructura o el sector encajan bien con nuestros socios.',
    },
    en: {
      question: 'What is the minimum funding amount you consider?',
      answer:
        'We generally focus on requirements from EUR 500,000 upwards, with no formal upper limit. Smaller requirements may be considered where the structure or sector is a strong fit for our partners.',
    },
  },
  {
    es: {
      question: '¿Exigen un pago por adelantado?',
      answer:
        'La consulta inicial y el análisis de elegibilidad no tienen coste. Cuando procede un mandato de preparación o estructuración, el alcance y el importe se documentan y acuerdan por adelantado. No hay cargos ocultos.',
    },
    en: {
      question: 'Do you require an upfront payment?',
      answer:
        'Initial consultations and eligibility reviews carry no charge. Where a preparation or packaging mandate is appropriate, the scope and fee are documented and agreed in advance. There are no hidden charges.',
    },
  },
  {
    es: {
      question: '¿Qué tipos de financiación pueden presentar?',
      answer:
        'Deuda senior y subordinada, circulante, financiación de activos y equipos, líneas de comercio y exportación, financiación de proyectos y construcción, estructuras mezzanine, private equity, capital riesgo y capital para joint ventures.',
    },
    en: {
      question: 'What types of funding can you introduce?',
      answer:
        'Senior and junior debt, working capital, asset and equipment finance, trade and export facilities, project and construction finance, mezzanine structures, private equity, venture capital and joint venture capital.',
    },
  },
  {
    es: {
      question: '¿Pueden ayudar si un banco ya me ha denegado la operación?',
      answer:
        'A menudo, sí. Una denegación suele reflejar un desajuste con los criterios del prestamista o una presentación deficiente, más que un negocio realmente no financiable. Revisamos los motivos y, si procede, reposicionamos el expediente hacia socios más adecuados.',
    },
    en: {
      question: 'Can you help if my business has been declined by a bank?',
      answer:
        'Often, yes. A decline frequently reflects a mismatch of lender criteria or a weak presentation rather than a fundamentally unfundable business. We review the reasons and, where appropriate, reposition the file for more suitable partners.',
    },
  },
  {
    es: {
      question: '¿Asisten en financiación inmobiliaria?',
      answer:
        'Sí. El inmobiliario es uno de nuestros sectores más activos: adquisición, promoción, financiación puente, refinanciación y activos comerciales en renta en múltiples jurisdicciones.',
    },
    en: {
      question: 'Do you assist with property and real estate funding?',
      answer:
        'Yes. Real estate is one of our most active sectors, covering acquisition, development, bridging, refinancing and income-producing commercial assets across multiple jurisdictions.',
    },
  },
  {
    es: {
      question: '¿Cómo seleccionan a sus socios financieros?',
      answer:
        'Cada socio se evalúa por su situación regulatoria, historial, apetito sectorial y operaciones realizadas antes de incorporarse a la red. Los socios que no cumplen nuestras expectativas de conducta son excluidos.',
    },
    en: {
      question: 'How do you select your funding partners?',
      answer:
        'Every partner is assessed for regulatory standing, track record, sector appetite and transaction history before joining our network. Partners who do not meet our conduct expectations are removed.',
    },
  },
  {
    es: {
      question: '¿Contactarán con prestamistas sin mi permiso?',
      answer:
        'Nunca. No se realiza ninguna aproximación ni se envía material hasta que usted haya aprobado tanto la lista de socios como la información que se va a compartir.',
    },
    en: {
      question: 'Will you contact lenders without my permission?',
      answer:
        'Never. No approach is made and no material is released until you have approved both the shortlist of partners and the information to be shared.',
    },
  },
  {
    es: {
      question: '¿Prestan asesoramiento financiero o de inversión?',
      answer:
        'Ofrecemos orientación comercial y de estructuración en relación con la preparación y la intermediación de financiación. No prestamos asesoramiento de inversión regulado, fiscal ni legal, y recomendamos que cada cliente cuente con sus propios asesores.',
    },
    en: {
      question: 'Do you provide financial or investment advice?',
      answer:
        'We provide commercial and structuring guidance in relation to funding preparation and introductions. We do not provide regulated investment advice, tax or legal advice, and we recommend that clients retain their own professional advisers.',
    },
  },
  {
    es: {
      question: '¿Puedo convertirme en socio financiero?',
      answer:
        'Sí. Damos la bienvenida a bancos, fondos de crédito, family offices, inversores privados y firmas de private equity y capital riesgo. Tras la debida diligencia, los socios cualificados reciben oportunidades ajustadas a su mandato.',
    },
    en: {
      question: 'Can I become a funding partner?',
      answer:
        'Yes. We welcome enquiries from banks, credit funds, family offices, private investors, private equity and venture capital firms. Following due diligence, qualified partners receive curated deal flow matched to their stated mandate.',
    },
  },
  {
    es: {
      question: '¿En qué divisas y jurisdicciones se estructuran las operaciones?',
      answer:
        'Las operaciones se estructuran habitualmente en EUR, USD, GBP y AED, con otras divisas disponibles según el socio. Las estructuras transfronterizas se evalúan caso por caso por motivos regulatorios y de cumplimiento.',
    },
    en: {
      question: 'What currencies and jurisdictions can transactions be structured in?',
      answer:
        'Transactions are commonly structured in EUR, USD, GBP and AED, with other currencies available depending on the partner. Cross-border structures are assessed case by case for regulatory and compliance suitability.',
    },
  },
  {
    es: {
      question: '¿Cómo gestionan el cumplimiento y la prevención de blanqueo de capitales?',
      answer:
        'Todos los clientes y socios se someten a verificación de identidad, indagación del origen de los fondos y cribado de sanciones. No continuamos cuando la información no puede verificarse o cuando la operación queda fuera de nuestro marco de cumplimiento.',
    },
    en: {
      question: 'How do you handle compliance and anti-money-laundering checks?',
      answer:
        'All clients and partners are subject to identity verification, source-of-funds enquiry and sanctions screening. We will not proceed where information cannot be verified or where a transaction falls outside our compliance framework.',
    },
  },
]

export const FUNDING_RANGES: L<string>[] = [
  { es: 'Menos de 500.000 €', en: 'Under EUR 500,000' },
  { es: '500.000 € – 2 millones', en: 'EUR 500,000 – 2 million' },
  { es: '2 – 10 millones €', en: 'EUR 2 – 10 million' },
  { es: '10 – 50 millones €', en: 'EUR 10 – 50 million' },
  { es: '50 – 250 millones €', en: 'EUR 50 – 250 million' },
  { es: 'Más de 250 millones €', en: 'Over EUR 250 million' },
]

export const PURPOSES: L<string>[] = [
  { es: 'Expansión del negocio', en: 'Business expansion' },
  { es: 'Circulante', en: 'Working capital' },
  { es: 'Adquisición de inmuebles', en: 'Property acquisition' },
  { es: 'Promoción inmobiliaria', en: 'Property development' },
  { es: 'Financiación de proyectos', en: 'Project finance' },
  { es: 'Financiación de comercio', en: 'Trade finance' },
  { es: 'Compra de equipamiento', en: 'Equipment purchase' },
  { es: 'Refinanciación', en: 'Refinancing' },
  { es: 'Adquisición / MBO', en: 'Acquisition / MBO' },
  { es: 'Inversión en capital', en: 'Equity investment' },
  { es: 'Otro', en: 'Other' },
]
